import { useState, useEffect } from 'react';
import { usePipeline } from '../../context/PipelineContext';
import { SERVICE_TIERS, LEAD_SOURCES } from '../../utils/pipelineCalculations';

const inputClass = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[4px] focus:outline-none focus:border-bullogic-blue';
const labelClass = 'block text-[11px] font-bold uppercase tracking-[0.08em] text-navy mb-1';

// Group service tiers by division
const TIERS_BY_DIVISION = {
  bwm: Object.entries(SERVICE_TIERS).filter(([, t]) => t.division === 'bwm'),
  bts: Object.entries(SERVICE_TIERS).filter(([, t]) => t.division === 'bts'),
  bia: Object.entries(SERVICE_TIERS).filter(([, t]) => t.division === 'bia'),
};

const DIVISIONS = [
  { value: 'bwm', label: 'BWM - Wealth Management' },
  { value: 'bts', label: 'BTS - Tax Services' },
  { value: 'bia', label: 'BIA - Insurance' },
];

export default function ProspectForm({ onClose, editingProspect }) {
  const { addProspect, updateProspect } = usePipeline();
  const isEditing = !!editingProspect;

  // Form state
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [division, setDivision] = useState('bwm');
  const [serviceTier, setServiceTier] = useState('guided-wealth');
  const [source, setSource] = useState('referral');
  const [expectedCloseDate, setExpectedCloseDate] = useState('');
  const [serviceStartDate, setServiceStartDate] = useState('');
  const [notes, setNotes] = useState('');

  // Value fields
  const [monthlyFee, setMonthlyFee] = useState('');
  const [oneTimeFee, setOneTimeFee] = useState('');
  const [aum, setAum] = useState('');
  const [aumFeeRate, setAumFeeRate] = useState('1');
  const [commissionEstimate, setCommissionEstimate] = useState('');

  // Populate form when editing
  useEffect(() => {
    if (editingProspect) {
      setName(editingProspect.name || '');
      setCompany(editingProspect.company || '');
      setEmail(editingProspect.email || '');
      setPhone(editingProspect.phone || '');
      setDivision(editingProspect.division || 'bwm');
      setServiceTier(editingProspect.serviceTier || 'guided-wealth');
      setSource(editingProspect.source || 'referral');
      setExpectedCloseDate(editingProspect.expectedCloseDate || '');
      setServiceStartDate(editingProspect.serviceStartDate || '');
      setNotes(editingProspect.notes || '');

      const ev = editingProspect.estimatedValue || {};
      setMonthlyFee(ev.monthlyRecurring ? String(ev.monthlyRecurring) : '');
      setOneTimeFee(ev.oneTime ? String(ev.oneTime) : '');
      setAum(ev.aum ? String(ev.aum) : '');
      setAumFeeRate(ev.aumFeeRate ? String(ev.aumFeeRate * 100) : '1');
      setCommissionEstimate(ev.oneTime ? String(ev.oneTime) : '');
    }
  }, [editingProspect]);

  // When division changes, reset service tier to first available
  useEffect(() => {
    const tiers = TIERS_BY_DIVISION[division] || [];
    if (tiers.length > 0) {
      // Only reset if current tier doesn't belong to this division
      const currentTierInfo = SERVICE_TIERS[serviceTier];
      if (!currentTierInfo || currentTierInfo.division !== division) {
        setServiceTier(tiers[0][0]);
      }
    }
  }, [division, serviceTier]);

  // Set default values when service tier changes (only for new prospects)
  useEffect(() => {
    if (isEditing) return;
    const tier = SERVICE_TIERS[serviceTier];
    if (!tier) return;

    if (tier.defaultMonthly > 0) setMonthlyFee(String(tier.defaultMonthly));
    else setMonthlyFee('');

    if (tier.defaultOneTime > 0) setOneTimeFee(String(tier.defaultOneTime));
    else setOneTimeFee('');
  }, [serviceTier, isEditing]);

  const currentTier = SERVICE_TIERS[serviceTier];
  const availableTiers = TIERS_BY_DIVISION[division] || [];

  // Determine which value fields to show based on service tier
  const showMonthlyFee = currentTier && (currentTier.revenueType === 'recurring' || currentTier.revenueType === 'recurring+onetime');
  const showOneTimeFee = currentTier && (currentTier.revenueType === 'onetime' || currentTier.revenueType === 'recurring+onetime');
  // AUM shows for tiers that explicitly have it, OR recurring BWM tiers
  // (Guided Wealth has optional AUM). The division guard keeps recurring
  // BTS tiers (e.g. Business Package) from showing an irrelevant AUM field.
  const showAum = currentTier && (currentTier.hasAUM || (currentTier.revenueType === 'recurring' && currentTier.division === 'bwm'));
  const isAumRequired = currentTier && currentTier.hasAUM;
  const showCommission = currentTier && currentTier.revenueType === 'commission';

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return;

    // Build estimatedValue based on tier type
    const estimatedValue = {};

    if (showMonthlyFee) {
      estimatedValue.monthlyRecurring = parseFloat(monthlyFee) || 0;
    }
    if (showOneTimeFee) {
      estimatedValue.oneTime = parseFloat(oneTimeFee) || 0;
    }
    if (showAum) {
      estimatedValue.aum = parseFloat(aum) || 0;
      estimatedValue.aumFeeRate = (parseFloat(aumFeeRate) || 0) / 100;
    }
    if (showCommission) {
      estimatedValue.oneTime = parseFloat(commissionEstimate) || 0;
    }

    const prospect = {
      name: name.trim(),
      company: company.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      division,
      serviceTier,
      source,
      expectedCloseDate: expectedCloseDate || null,
      serviceStartDate: serviceStartDate || null,
      notes: notes.trim() || null,
      estimatedValue,
    };

    if (isEditing) {
      updateProspect(editingProspect.id, prospect);
    } else {
      addProspect(prospect);
    }

    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-[4px] shadow-[0_8px_30px_rgba(0,0,0,0.15)] w-[640px] max-w-full max-h-[90vh] overflow-y-auto p-5 border border-bullogic-blue/30">
      <h3 className="text-[14px] font-bold text-navy mb-4">
        {isEditing ? 'Edit Prospect' : 'Add Prospect'}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Name and Company */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., John Smith"
              className={inputClass}
              required
            />
          </div>
          <div>
            <label className={labelClass}>Company (optional)</label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g., Smith Enterprises"
              className={inputClass}
            />
          </div>
        </div>

        {/* Email and Phone — required for AC nurture sequences and the
            rebrand email broadcast. SF sync auto-fills these from the
            primary contact on the Opportunity, but allow manual entry
            here for prospects added directly in BCC. */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prospect@example.com"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Phone (optional)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="555-555-5555"
              className={inputClass}
            />
          </div>
        </div>

        {/* Division and Service Tier */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Division</label>
            <select
              value={division}
              onChange={(e) => setDivision(e.target.value)}
              className={inputClass}
            >
              {DIVISIONS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Service Tier</label>
            <select
              value={serviceTier}
              onChange={(e) => setServiceTier(e.target.value)}
              className={inputClass}
            >
              {availableTiers.map(([key, tier]) => (
                <option key={key} value={key}>{tier.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Source and Expected Close */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Source</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className={inputClass}
            >
              {LEAD_SOURCES.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Expected Close Date</label>
            <input
              type="date"
              value={expectedCloseDate}
              onChange={(e) => setExpectedCloseDate(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Service Start Date</label>
            <input
              type="date"
              value={serviceStartDate}
              onChange={(e) => setServiceStartDate(e.target.value)}
              className={inputClass}
            />
            <div className="text-[10px] text-[#888] mt-1">
              When the engagement actually begins. Defaults to expected close date if blank.
            </div>
          </div>
        </div>

        {/* Dynamic value fields based on service tier */}
        <div className="border-t border-gray-200 pt-3 mt-3">
          <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-deep-teal mb-2">
            Revenue Details
          </div>

          {/* BWM Guided: monthly fee, optional AUM */}
          {showMonthlyFee && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={labelClass}>Monthly Fee ($)</label>
                <input
                  type="number"
                  value={monthlyFee}
                  onChange={(e) => setMonthlyFee(e.target.value)}
                  placeholder={currentTier.defaultMonthly > 0 ? String(currentTier.defaultMonthly) : '0'}
                  className={inputClass}
                />
              </div>
              {showOneTimeFee && (
                <div>
                  <label className={labelClass}>One-Time Fee ($)</label>
                  <input
                    type="number"
                    value={oneTimeFee}
                    onChange={(e) => setOneTimeFee(e.target.value)}
                    placeholder={currentTier.defaultOneTime > 0 ? String(currentTier.defaultOneTime) : '0'}
                    className={inputClass}
                  />
                </div>
              )}
            </div>
          )}

          {/* One-time only (BTS) */}
          {showOneTimeFee && !showMonthlyFee && (
            <div className="mb-3">
              <label className={labelClass}>One-Time Fee ($)</label>
              <input
                type="number"
                value={oneTimeFee}
                onChange={(e) => setOneTimeFee(e.target.value)}
                placeholder={currentTier.defaultOneTime > 0 ? String(currentTier.defaultOneTime) : '0'}
                className={inputClass}
              />
            </div>
          )}

          {/* AUM fields */}
          {showAum && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className={labelClass}>
                  AUM ($){isAumRequired ? ' *' : ' (optional)'}
                </label>
                <input
                  type="number"
                  value={aum}
                  onChange={(e) => setAum(e.target.value)}
                  placeholder="500000"
                  className={inputClass}
                  required={isAumRequired}
                />
              </div>
              <div>
                <label className={labelClass}>AUM Fee Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={aumFeeRate}
                  onChange={(e) => setAumFeeRate(e.target.value)}
                  placeholder="1"
                  className={inputClass}
                />
              </div>
            </div>
          )}

          {/* Commission (BIA) */}
          {showCommission && (
            <div className="mb-3">
              <label className={labelClass}>Commission Estimate ($)</label>
              <input
                type="number"
                value={commissionEstimate}
                onChange={(e) => setCommissionEstimate(e.target.value)}
                placeholder="2000"
                className={inputClass}
              />
            </div>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className={labelClass}>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any relevant details..."
            rows={2}
            className={inputClass}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            className="px-4 py-2 text-[12px] font-bold text-white bg-bullogic-blue rounded-[4px] hover:bg-bullogic-blue/90"
          >
            {isEditing ? 'Update Prospect' : 'Add Prospect'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[12px] font-bold text-[#888] bg-gray-100 rounded-[4px] hover:bg-gray-200"
          >
            Cancel
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
